import { useState, useEffect } from 'react';
import { collection, onSnapshot, writeBatch, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  BANK_ACCOUNTS, CLASSIFICATIONS, TRANSACTION_TYPES, DEFAULT_MONTHLY_BUDGETS,
} from '../data/accounts';

// In-memory lock so concurrent hook instances don't double-seed
const seedingInProgress = {};

const seedIfEmpty = async (orgId) => {
  if (seedingInProgress[orgId]) return;
  seedingInProgress[orgId] = true;

  try {
    const seededDoc = await getDoc(doc(db, 'orgs', orgId, '_config', 'seeded'));
    if (seededDoc.exists()) return;

    const batch = writeBatch(db);

    BANK_ACCOUNTS.forEach((name, i) => {
      batch.set(doc(collection(db, 'orgs', orgId, 'accounts')), { name, active: true, order: i });
    });

    CLASSIFICATIONS.forEach((name, i) => {
      batch.set(doc(collection(db, 'orgs', orgId, 'funds')), { name, active: true, order: i });
    });

    TRANSACTION_TYPES.forEach((t, i) => {
      batch.set(doc(collection(db, 'orgs', orgId, 'transactionTypes')), {
        name: t.name,
        fund: t.classification,
        kind: t.kind,
        monthlyBudget: DEFAULT_MONTHLY_BUDGETS[t.name] || 0,
        active: true,
        order: i,
      });
    });

    await batch.commit();
    await setDoc(doc(db, 'orgs', orgId, '_config', 'seeded'), { at: new Date().toISOString() });
  } finally {
    seedingInProgress[orgId] = true; // keep true — seeded for this session
  }
};

export const useOrgData = (orgId) => {
  const [accounts, setAccounts] = useState([]);
  const [funds, setFunds] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    let unsubs = [];
    let mounted = true;

    seedIfEmpty(orgId).catch(console.error).then(() => {
      if (!mounted) return;

      const byOrder = (a, b) => (a.order ?? 99) - (b.order ?? 99);

      unsubs.push(onSnapshot(collection(db, 'orgs', orgId, 'accounts'), (snap) => {
        setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort(byOrder));
      }));

      unsubs.push(onSnapshot(collection(db, 'orgs', orgId, 'funds'), (snap) => {
        setFunds(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort(byOrder));
      }));

      unsubs.push(onSnapshot(collection(db, 'orgs', orgId, 'transactionTypes'), (snap) => {
        setTransactionTypes(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort(byOrder));
        setLoading(false);
      }));
    });

    return () => { mounted = false; unsubs.forEach(u => u()); };
  }, [orgId]);

  return { accounts, funds, transactionTypes, loading };
};
